/**
 * Scan report: engine names, detection classes, and what moves the status.
 *
 * One place for three things the report dialog used to work out inline, each of which had gone
 * quietly wrong at least once:
 *
 *   1. which engine ROW a finding belongs to (findings say "clamav", the summary says "ClamAV");
 *   2. which CLASS a finding is (read from detection_class, never from its title or its engine);
 *   3. which headline the report carries (the scanner's status and class counts, never the AI's
 *      opinion and never severity).
 *
 * The report is decision support and may be relied on in litigation: nothing here may over-state
 * a finding as confirmed, and nothing here may let an opinion move the status.
 */

// ── 1. Engine rows ───────────────────────────────────────────────────────────────────────────
//
// The scanner's `engines` map (scanner 2270c27, 2 Oct 2026) has 18 rows on every scan. Findings
// carry the engine's snake_case name; the rows are the summary's names. An explicit table, not a
// case-conversion rule: `clamav`, `yara`, `stix_feeds`, `mbr_analysis`, `ad_analysis` and
// `claude` cannot be derived by one, and the old rule (convert only names with an underscore)
// left `clamav`, `yara` and `rootkit` findings unmatched to their rows.
//
// Keyed on the name lowercased with `_`, `-` and spaces removed, so a finding's name
// ("ad_analysis"), today's row ("ADAnalysis"), the row older scans sent ("ad_analysis") and the
// row this UI once derived ("AdAnalysis") all resolve to the same thing.
const ENGINE_ROWS: Record<string, string> = {
    clamav: 'ClamAV',
    bitdefender: 'Bitdefender',
    yara: 'YARA',
    hashlookup: 'HashLookup',
    stixfeeds: 'ThreatIntel',
    threatintel: 'ThreatIntel',
    ransomnotes: 'RansomNotes',
    rootkit: 'Rootkit',
    mbranalysis: 'MBR',
    mbr: 'MBR',
    structuralvalidation: 'StructuralValidation',
    entropy: 'Entropy',
    wholefileentropy: 'WholeFileEntropy',
    filesystemanomaly: 'FilesystemAnomaly',
    ransomextensions: 'RansomExtensions',
    binaryanalysis: 'BinaryAnalysis',
    dbriskpatterns: 'DbRiskPatterns',
    adanalysis: 'ADAnalysis',
    activedirectory: 'ADAnalysis',
    adaudit: 'ADAnalysis',
    ad: 'ADAnalysis',
    eventlog: 'EventLog',
    configdiff: 'ConfigDiff',
    // The AI: findings are written with engine='claude', the summary calls the same thing 'Bedrock'.
    claude: 'Bedrock',
    bedrock: 'Bedrock',
    ai: 'Bedrock',
}

/** The engine row a finding's engine name, or a row name in any of its spellings, refers to. */
export function canonicalEngine(name: unknown): string {
    const raw = String(name ?? '').trim()
    if (!raw) return ''
    const row = ENGINE_ROWS[raw.toLowerCase().replace(/[_\s-]/g, '')]
    if (row) return row
    // An engine this table has not met: keep it readable rather than drop it. `pipeline` lands
    // here on purpose - it marks a pipeline that failed, and has no row.
    if (!raw.includes('_')) return raw
    return raw.split('_').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('')
}

/** Row name → what the report calls it. Anything absent shows its row name. */
export const ENGINE_DISPLAY_NAMES: Record<string, string> = {
    Bedrock: 'AI Analysis',
    ADAnalysis: 'Active Directory',
    DbRiskPatterns: 'Database risk patterns',
    StructuralValidation: 'Structural validation',
    WholeFileEntropy: 'Whole-file entropy',
}

export function engineLabel(name: unknown): string {
    const row = canonicalEngine(name)
    return ENGINE_DISPLAY_NAMES[row] || row || String(name ?? '')
}

/**
 * The engines that CAN report a confirmed detection (the scanner's own list). It marks a row
 * "IOC", and it is the class of last resort for a finding with no detection_class - only scans
 * from before the class model.
 *
 * Capable is not the same as confirmed: YARA and Rootkit report far more indicators than
 * confirmed detections, so this set never decides a colour for a finding that carries a class.
 * RansomExtensions is not here: the scanner classes it as an indicator (an extension is trivially
 * renamed, and many have legitimate uses).
 */
export const CONFIRMED_CAPABLE_ENGINES: ReadonlySet<string> = new Set([
    'ClamAV', 'Bitdefender', 'YARA', 'HashLookup', 'ThreatIntel', 'RansomNotes', 'Rootkit', 'MBR',
    'StructuralValidation',
])

export function isConfirmedCapable(name: unknown): boolean {
    return CONFIRMED_CAPABLE_ENGINES.has(canonicalEngine(name))
}

// ── 2. Detection class ───────────────────────────────────────────────────────────────────────
//
//   class       produced by               moves the status?
//   confirmed   detection engines only    yes - threats found
//   indicator   detection engines only    yes - indicators only
//   telemetry   any                       no
//   posture     AI, AD analysis           no
//   advisory    AI, config diff           no
//
// posture (scanner df8a055, 9 Sept) is a configuration weakness that predates any incident.
//
// advisory (scanner 2270c27, 2 Oct) is an interpretation or an observed change, NOT a detection:
// the AI reading configuration text and giving an opinion about intent, or the config diff
// reporting that something changed. On one Switch1 image the AI called the same privileged
// account hygiene in one scan and attacker activity in the next two. Before the class existed
// those findings fell through to `indicator`, so the scanner reported "No confirmed detections"
// while this UI counted them under Indicators - banner and tiles disagreeing on one report.
//
// Rows scanned before a class existed are NOT re-read into it: they were classified under the
// rules of their day, and relabelling them would rewrite the status history of objects nobody
// re-examined.
export type DetectionClass = 'confirmed' | 'indicator' | 'telemetry' | 'posture' | 'advisory'

const CLASSES: ReadonlySet<string> = new Set(['confirmed', 'indicator', 'telemetry', 'posture', 'advisory'])

/**
 * A finding's class: its detection_class, and nothing else when it has one. Never the title -
 * the same sentence is posture or an indicator depending on what changed since the baseline.
 *
 * Without one (a scan from before the class model) the engine is the only evidence left: a
 * confirmed-capable engine's finding is confirmed, anything else an indicator. An unrecognised
 * class degrades to indicator, the safe direction - it neither claims a confirmed detection nor
 * files evidence away.
 */
export function detectionClassOf(f: any): DetectionClass {
    const dc = String(f?.detection_class || '').toLowerCase()
    if (CLASSES.has(dc)) return dc as DetectionClass
    if (!dc && isConfirmedCapable(f?.engine)) return 'confirmed'
    return 'indicator'
}

// ── 3. The headline ──────────────────────────────────────────────────────────────────────────

export type DispositionKey = 'threat' | 'info' | 'clean' | 'error' | 'incomplete'

function reportFindings(report: any): any[] {
    const list = report?.findings || report?.top_findings
    return Array.isArray(list) ? list : []
}

/**
 * The engines that did not finish EXAMINING the backup, by the name the report shows.
 *
 * The scanner's own word is taken for it: `degraded_engines`, which it works out fail-closed
 * from the engines that examine the backup and which is on every report since early September
 * 2026. When the field is there - even empty - nothing else is consulted.
 *
 * For an older report without the field: any engine row at failed or error. "Not run",
 * "skipped" and "not applicable" are not failures - the engine had nothing to do here.
 *
 * Never the AI analyst (the Scanner session, 8 Oct 2026). It is advisory: it interprets what
 * the other engines found and cannot move a scan's status either way. Its row can read error
 * while the scan is whole - the report says the AI review did not run, beside the AI summary,
 * and that is all it means.
 */
export function failedEngineLabels(report: any): string[] {
    const rows = new Set<string>()
    if (Array.isArray(report?.degraded_engines)) {
        for (const name of report.degraded_engines) rows.add(canonicalEngine(name))
    } else {
        for (const [name, e] of Object.entries(report?.engines || {}) as Array<[string, any]>) {
            const status = String(e?.status || '').toLowerCase()
            if (status === 'failed' || status === 'error') rows.add(canonicalEngine(name))
        }
    }
    rows.delete('')
    rows.delete('Bedrock')
    return [...rows].map((row) => ENGINE_DISPLAY_NAMES[row] || row)
}

/**
 * Which headline the report carries. Decision SUPPORT, never a safety verdict: 'clean' is
 * "No confirmed detections", not a clean bill of health.
 *
 * In order:
 *   1. error       the scan did not run (its status, or every engine failed).
 *   2. threat      a confirmed detection. Even in a scan that did not finish, what it did find
 *                  is found.
 *   3. incomplete  the scanner says the scan was incomplete, or lists an engine that did not
 *                  finish examining the backup (failedEngineLabels; never the AI analyst). A scan
 *                  with a failed engine must NEVER read as a completed one: "Indicators only" or "No
 *                  confirmed detections" over files nobody examined is the misreading this
 *                  report exists to prevent. (Found 8 Oct 2026: AD2, status 'incomplete' with
 *                  FileEnumeration failed - 5 directories unread - headed "Indicators only".)
 *   4. info / clean  from the class counts. Only `confirmed` and `indicator` are consulted -
 *                  telemetry, posture and advisory never move the status, whatever their severity.
 *
 * The AI's `assessment` is NOT consulted. It used to be, on equal terms with the status, so an
 * opinion could turn "No confirmed detections" into "Indicators only". The AI cannot produce a
 * confirmed or indicator finding; it cannot produce the headline either.
 *
 * A report with neither a status this knows nor class counts is counted from what it does carry:
 * its engines block, then its findings list.
 */
export function dispositionKeyOf(report: any): DispositionKey {
    const engines = Object.entries(report?.engines || {}) as Array<[string, any]>
    const status = String(report?.status || '').toLowerCase()
    const allFailed = engines.length > 0 && engines.every(([, e]) => e?.status === 'not_run' || e?.status === 'error')
    if (status === 'error' || allFailed) return 'error'

    const counts = report?.summary?.class_counts
    const hasCounts = !!counts && typeof counts === 'object'
    const findings = reportFindings(report)

    if (status === 'threats_found') return 'threat'
    if (hasCounts) {
        if (Number(counts.confirmed ?? 0) > 0) return 'threat'
    } else if (status !== 'informational' && status !== 'clean') {
        if (engines.some(([n, e]) => isConfirmedCapable(n) && Number(e?.findings || 0) > 0)) return 'threat'
        if (findings.some((f) => detectionClassOf(f) === 'confirmed')) return 'threat'
    }

    if (status === 'incomplete' || failedEngineLabels(report).length > 0) return 'incomplete'

    if (hasCounts) return status === 'informational' || Number(counts.indicator ?? 0) > 0 ? 'info' : 'clean'
    if (status === 'informational') return 'info'
    if (status === 'clean') return 'clean'
    return findings.some((f) => detectionClassOf(f) === 'indicator') ? 'info' : 'clean'
}

/**
 * The engines named in "Confirmed detection(s) reported by: …".
 *
 * From the findings where they carry a class: the engines with a finding classed confirmed. An
 * engine's findings COUNT says nothing about class - YARA with 171 heuristic matches beside one
 * ClamAV signature hit is not two engines reporting confirmed detections - so the count is only
 * used for a report whose findings carry no class at all, or whose (truncated) list shows none.
 */
export function confirmedEngineLabels(report: any): string[] {
    const fromFindings = new Map<string, number>()
    for (const f of reportFindings(report)) {
        if (!f?.detection_class || detectionClassOf(f) !== 'confirmed') continue
        const row = canonicalEngine(f.engine)
        fromFindings.set(row, (fromFindings.get(row) || 0) + 1)
    }
    if (fromFindings.size > 0) return [...fromFindings.keys()].map((row) => ENGINE_DISPLAY_NAMES[row] || row)

    return (Object.entries(report?.engines || {}) as Array<[string, any]>)
        .filter(([n, e]) => isConfirmedCapable(n) && Number(e?.findings || 0) > 0)
        .map(([n, e]) => `${engineLabel(n)} (${e.findings})`)
}
